import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { buildPlaceholderRun, useRunManager } from '../hooks/useRunManager';
import ConfigPanel from './ConfigPanel';
import ProgressPanel from './ProgressPanel';
import ResultsPanel from './ResultsPanel';
import HistoryPanel from './HistoryPanel';

const GraphCanvas = lazy(() => import('./GraphCanvas'));

function formatLaunchError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return '请求已发送，但启动任务失败。请检查后端服务或稍后重试。';
}

export default function AppShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const activeView = useAppStore((state) => state.activeView);
  const setActiveView = useAppStore((state) => state.setActiveView);
  const language = useAppStore((state) => state.language);
  const setLanguage = useAppStore((state) => state.setLanguage);
  const currentRun = useAppStore((state) => state.currentRun);
  const setCurrentRun = useAppStore((state) => state.setCurrentRun);
  const addToHistory = useAppStore((state) => state.addToHistory);
  const { launchRun, subscribeSSE, refreshRun, cleanup } = useRunManager();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const isCompleted = currentRun?.status === 'completed';
  const isKgBuildPhase = Boolean(currentRun && !isCompleted && currentRun.currentStage === 'data_analyst');
  const canvasScene = isCompleted ? 'results' : isKgBuildPhase ? 'kg-build' : currentRun ? 'progress' : 'idle';

  const handleLaunch = async (payload: Record<string, unknown>) => {
    if (loading) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const runId = await launchRun(payload);
      if (!runId) {
        throw new Error('启动任务失败。');
      }

      const optimisticRun = buildPlaceholderRun({
        runId,
        topic: String(payload.topic || '').trim(),
        status: 'queued',
        progress: 2,
        message: '任务已提交，正在建立实时连接。',
      });

      setCurrentRun(optimisticRun);
      addToHistory({
        runId,
        topic: optimisticRun.topic,
        status: optimisticRun.status,
        createdAt: optimisticRun.createdAt,
        progress: optimisticRun.progress,
      });

      subscribeSSE(runId);
      void refreshRun(runId);
    } catch (e) {
      cleanup();
      setCurrentRun(null);
      setErrorMsg(`启动失败：${formatLaunchError(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleLang = () => {
    const next = language === 'zh' ? 'en' : 'zh';
    setLanguage(next);
    i18n.changeLanguage(next);
  };

  const handleNewTask = () => {
    cleanup();
    setCurrentRun(null);
    setErrorMsg('');
    setLoading(false);
    setActiveView('workspace');
    navigate('/');
  };

  return (
    <div className="app-shell">
      {/* Top navigation — always visible */}
      <nav className="top-nav">
        <div className="nav-left">
          <h1 className="nav-brand">{t('app.title')}</h1>
          <span className="nav-subtitle">{t('app.subtitle')}</span>
        </div>
        <div className="nav-right">
          <button className={`nav-btn ${activeView === 'workspace' ? 'active' : ''}`}
            onClick={handleNewTask}>{t('nav.newRun')}</button>
          <button className={`nav-btn ${activeView === 'history' ? 'active' : ''}`}
            onClick={() => setActiveView('history')}>{t('nav.history')}</button>
          <button className="nav-btn" onClick={() => navigate('/account')}>账户</button>
          <button className="lang-btn" onClick={toggleLang}>{language === 'zh' ? 'EN' : '中文'}</button>
        </div>
      </nav>

      {/* Full-screen starfield background */}
      {activeView !== 'history' && (
        <Suspense fallback={null}>
          <GraphCanvas runStatus={currentRun?.currentStage || ''} scene={canvasScene} />
        </Suspense>
      )}

      {/* History view */}
      {activeView === 'history' ? (
        <main className="main-content" style={{ position: 'relative', zIndex: 2 }}>
          <HistoryPanel />
        </main>
      ) : (
        <>
          {/* Center config card (idle) with full-screen starfield behind */}
          {!currentRun && (
            <div className="center-layout" style={{ position: 'relative', zIndex: 2 }}>
              <ConfigPanel onLaunch={handleLaunch} submitState={loading ? 'launching' : 'idle'} />
              {errorMsg && <div className="center-error">{errorMsg}</div>}
            </div>
          )}

          {/* Running pipeline — progress bar at bottom */}
          {currentRun && currentRun.status !== 'completed' && (
            <main className={`main-content ${isKgBuildPhase ? 'progress-kg-layout' : 'progress-center-layout'}`} style={{ position: 'relative', zIndex: 2 }}>
              <div className={`progress-floating-shell ${isKgBuildPhase ? 'progress-floating-shell-docked' : ''}`}>
                <ProgressPanel run={currentRun} />
              </div>
            </main>
          )}

          {/* Completed pipeline — review panel in center */}
          {currentRun && currentRun.status === 'completed' && (
            <main className="main-content review-center-layout" style={{ position: 'relative', zIndex: 2 }}>
              <ResultsPanel run={currentRun} />
            </main>
          )}
        </>
      )}
    </div>
  );
}
